import { RouteSeo, seoForPath } from './route-seo';

const JSON_LD_ID = 'bh-route-jsonld';

const upsertMeta = (
  doc: Document,
  attr: 'name' | 'property',
  key: string,
  content: string
) => {
  let el = doc.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = doc.createElement('meta');
    el.setAttribute(attr, key);
    doc.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

/**
 * Writes route metadata into <head>: robots, canonical, description, Open
 * Graph / Twitter, JSON-LD. The title is owned here only for indexable pages;
 * app/private pages keep the titles their screens set.
 */
export const applySeo = (seo: RouteSeo, doc: Document = document): void => {
  if (seo.indexable) doc.title = seo.title;
  upsertMeta(doc, 'name', 'description', seo.description);
  upsertMeta(doc, 'name', 'robots', seo.robots);
  upsertMeta(doc, 'property', 'og:title', seo.title);
  upsertMeta(doc, 'property', 'og:description', seo.description);
  upsertMeta(doc, 'name', 'twitter:title', seo.title);
  upsertMeta(doc, 'name', 'twitter:description', seo.description);

  let canonical = doc.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (seo.canonical) {
    if (!canonical) {
      canonical = doc.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      doc.head.appendChild(canonical);
    }
    canonical.setAttribute('href', seo.canonical);
    upsertMeta(doc, 'property', 'og:url', seo.canonical);
  } else {
    // A private/non-indexable URL must not claim a canonical (and must not
    // inherit the home page's from index.html).
    canonical?.remove();
    doc.head.querySelector('meta[property="og:url"]')?.remove();
  }

  doc.getElementById(JSON_LD_ID)?.remove();
  if (seo.jsonLd) {
    const script = doc.createElement('script');
    script.type = 'application/ld+json';
    script.id = JSON_LD_ID;
    script.textContent = JSON.stringify(seo.jsonLd);
    doc.head.appendChild(script);
  }
};

export const applySeoForPath = (pathname: string, doc: Document = document) =>
  applySeo(seoForPath(pathname), doc);
